import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, Trash, WarningCircle } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const SHAPE_OPTIONS = ["Rectangle", "L-shape", "L-shape right", "U-shape", "Island"];
const TYPE_OPTIONS = ["countertop", "backsplash", "waterfall"];
const CUT_KINDS = ["undermount sink", "drop-in sink", "farmhouse sink", "cooktop", "faucet"];

const field =
  "h-8 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-100 outline-none focus:border-indigo-500";

/** Review what the AI read from a sketch photo — fix sizes/shapes — before it lands on the drawing. */
export const StudioAiReviewDialog = ({ review, onClose, onAdd }) => {
  const [rows, setRows] = useState(() =>
    (review?.pieces || []).map((p, i) => ({ ...p, _i: i, include: true })),
  );
  const [notes, setNotes] = useState(review?.notes || "");
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(() =>
    review?.file ? URL.createObjectURL(review.file) : "",
  );

  const patch = (i, p) => setRows((r) => r.map((x) => (x._i === i ? { ...x, ...p } : x)));
  const patchCut = (i, ci, p) =>
    patch(i, { cutouts: rows[i].cutouts.map((c, k) => (k === ci ? { ...c, ...p } : c)) });

  const recheck = async () => {
    if (!review?.file) return;
    setChecking(true);
    const fd = new FormData();
    fd.append("file", review.file);
    fd.append(
      "previous",
      JSON.stringify({
        pieces: rows.map(({ _i, include, ...p }) => p),
        notes,
      }),
    );
    try {
      const { data } = await api.post("/studio/analyze-drawing", fd);
      setRows((data.pieces || []).map((p, i) => ({ ...p, _i: i, include: true })));
      setNotes(data.notes || "");
      toast.success("AI re-checked the sizes and shapes");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not re-check the drawing",
      );
    }
    setChecking(false);
  };

  const chosen = rows.filter((r) => r.include);

  return (
    <Dialog open={!!review} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="studio-ai-review"
        className="max-h-[92vh] w-[96vw] max-w-4xl overflow-y-auto border-zinc-800 bg-zinc-950 text-zinc-100"
      >
        <DialogHeader>
          <DialogTitle className="text-base">Check what the AI read</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Fix any size or shape below, untick what you don't want, then add to the drawing.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_240px]">
          <div className="min-w-0 space-y-3">
            {rows.map((p) => (
              <div
                key={p._i}
                data-testid={`ai-review-piece-${p._i}`}
                className={`rounded-lg border p-3 ${
                  p.include ? "border-zinc-800 bg-zinc-900" : "border-zinc-900 bg-zinc-950 opacity-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid={`ai-review-include-${p._i}`}
                    checked={p.include}
                    onChange={(e) => patch(p._i, { include: e.target.checked })}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <input
                    data-testid={`ai-review-label-${p._i}`}
                    value={p.label}
                    onChange={(e) => patch(p._i, { label: e.target.value })}
                    className={`${field} flex-1 font-semibold`}
                  />
                  {p.guessed && (
                    <span
                      data-testid={`ai-review-guessed-${p._i}`}
                      className="flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-300"
                    >
                      <WarningCircle size={12} weight="bold" /> Guessed
                    </span>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
                  {[
                    ["length_in", "Length (in)"],
                    ["depth_in", "Depth (in)"],
                  ].map(([k, label]) => (
                    <label key={k} className="block">
                      <span className="block text-[9px] uppercase tracking-widest text-zinc-500">
                        {label}
                      </span>
                      <input
                        type="number"
                        step="0.125"
                        data-testid={`ai-review-${k}-${p._i}`}
                        value={p[k]}
                        onChange={(e) => patch(p._i, { [k]: Number(e.target.value) })}
                        className={field}
                      />
                    </label>
                  ))}
                  <label className="block">
                    <span className="block text-[9px] uppercase tracking-widest text-zinc-500">
                      Shape
                    </span>
                    <select
                      data-testid={`ai-review-shape-${p._i}`}
                      value={SHAPE_OPTIONS.includes(p.shape) ? p.shape : "Rectangle"}
                      onChange={(e) => patch(p._i, { shape: e.target.value })}
                      className={field}
                    >
                      {SHAPE_OPTIONS.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[9px] uppercase tracking-widest text-zinc-500">
                      Type
                    </span>
                    <select
                      data-testid={`ai-review-type-${p._i}`}
                      value={p.type}
                      onChange={(e) => patch(p._i, { type: e.target.value })}
                      className={field}
                    >
                      {TYPE_OPTIONS.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[9px] uppercase tracking-widest text-zinc-500">
                      Room
                    </span>
                    <input
                      data-testid={`ai-review-room-${p._i}`}
                      value={p.room || ""}
                      onChange={(e) => patch(p._i, { room: e.target.value })}
                      className={field}
                    />
                  </label>
                </div>
                {(p.cutouts || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {p.cutouts.map((c, ci) => (
                      <div key={ci} className="flex items-center gap-1.5 text-xs">
                        <select
                          data-testid={`ai-review-cut-kind-${p._i}-${ci}`}
                          value={CUT_KINDS.includes(c.kind) ? c.kind : "undermount sink"}
                          onChange={(e) => patchCut(p._i, ci, { kind: e.target.value })}
                          className={`${field} w-40`}
                        >
                          {CUT_KINDS.map((k) => (
                            <option key={k}>{k}</option>
                          ))}
                        </select>
                        <span className="text-zinc-500">at</span>
                        <input
                          type="number"
                          data-testid={`ai-review-cut-x-${p._i}-${ci}`}
                          value={c.center_x_in > 0 ? c.center_x_in : ""}
                          placeholder="x"
                          onChange={(e) => patchCut(p._i, ci, { center_x_in: Number(e.target.value) })}
                          className={`${field} w-16`}
                        />
                        <input
                          type="number"
                          data-testid={`ai-review-cut-y-${p._i}-${ci}`}
                          value={c.center_y_in > 0 ? c.center_y_in : ""}
                          placeholder="y"
                          onChange={(e) => patchCut(p._i, ci, { center_y_in: Number(e.target.value) })}
                          className={`${field} w-16`}
                        />
                        <span className="text-zinc-500">in from left / back</span>
                        <button
                          type="button"
                          aria-label="Remove cut-out"
                          data-testid={`ai-review-cut-remove-${p._i}-${ci}`}
                          onClick={() =>
                            patch(p._i, { cutouts: p.cutouts.filter((_, k) => k !== ci) })
                          }
                          className="ml-auto p-1 text-zinc-600 hover:text-red-400"
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {notes && (
              <p data-testid="ai-review-notes" className="text-xs leading-relaxed text-zinc-400">
                AI notes: {notes}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {preview && (
              <img
                src={preview}
                alt="Uploaded sketch"
                data-testid="ai-review-preview"
                className="w-full rounded-lg border border-zinc-800 object-contain"
                onLoad={() => setPreview((u) => u)}
              />
            )}
            <button
              type="button"
              data-testid="ai-review-recheck"
              disabled={checking}
              onClick={recheck}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-indigo-400/50 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/15 disabled:opacity-40"
            >
              <Sparkle size={13} weight="bold" className={checking ? "animate-pulse" : ""} />
              {checking ? "Checking…" : "Re-check sizes with AI"}
            </button>
            <p className="text-[11px] leading-snug text-zinc-500">
              The AI re-reads every dimension digit by digit and confirms each shape against the
              photo, then tells you what it changed.
            </p>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap justify-end gap-2 border-t border-zinc-800 pt-3">
          <button
            type="button"
            data-testid="ai-review-cancel"
            onClick={onClose}
            className="rounded-full border border-zinc-700 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-100"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="ai-review-add"
            disabled={!chosen.length}
            onClick={() => onAdd(chosen.map(({ _i, include, ...p }) => p))}
            className="rounded-full bg-indigo-500 px-5 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
          >
            Add {chosen.length} piece{chosen.length === 1 ? "" : "s"} to drawing
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
